import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

export const SEO_STATUSES = [
  "work_in_progress",
  "completed",
  "waiting_for_approval",
] as const;
export type SeoStatus = (typeof SEO_STATUSES)[number];
export const SEO_STATUS_LABEL: Record<SeoStatus, string> = {
  work_in_progress: "Work in Progress",
  completed: "Completed",
  waiting_for_approval: "Waiting for Approval",
};

/** Lookup list key used for the Tools Used multiselect. */
export const SEO_TOOLS_LOOKUP_KEY = "seo_tools";

/** All SEO category keys (active + retained for legacy data). */
// SOURCE OF TRUTH: also maintained in lookup_items
// table. When adding new values, add to BOTH here
// and lookup_items, or the DB will accept it but
// the dropdown won't show it.
export const SEO_CATEGORIES = [
  "blog_writing",
  "blog_published",
  "blog_research",
  "backlink_research",
  "keyword_research",
  "backlinks_created",
  "competitor_analysis",
  "website_content",
  "shopify_edit",
  "seo_audit",
  "idea_documentation",
  "other",
  // retained for legacy rows only — not shown in the new dropdown
  "backlink_tracking",
  "backlink_outreach",
  "social_media_tracking",
  "traffic_analysis",
  "gsc_check",
  "image_generation",
] as const;
export type SeoCategory = (typeof SEO_CATEGORIES)[number];

/** Categories shown in the New Entry dropdown — exact order. */
export const SEO_ACTIVE_CATEGORIES = [
  "blog_writing",
  "blog_published",
  "blog_research",
  "backlink_research",
  "keyword_research",
  "backlinks_created",
  "competitor_analysis",
  "website_content",
  "shopify_edit",
  "seo_audit",
  "idea_documentation",
  "other",
] as const satisfies readonly SeoCategory[];

/** Aggregate categories — Status hidden, default to "completed". */
export const SEO_AGGREGATE_CATEGORIES = new Set<SeoCategory>([
  "backlinks_created",
  "backlink_outreach",
  "social_media_tracking",
  "traffic_analysis",
  "gsc_check",
  "image_generation",
]);

/** Read-only legacy categories — never shown in the dropdown for new entries. */
export const SEO_LEGACY_CATEGORIES = [
  "backlink_tracking",
  "backlink_outreach",
  "social_media_tracking",
  "traffic_analysis",
  "gsc_check",
  "image_generation",
] as const;
export type SeoLegacyCategory = (typeof SEO_LEGACY_CATEGORIES)[number];

export type AnySeoCategory = SeoCategory;

export const SEO_CATEGORY_LABEL: Record<AnySeoCategory, string> = {
  blog_writing: "Blog Writing",
  blog_published: "Blog Published",
  blog_research: "Blog Research / Topic Ideation",
  backlink_research: "Backlink Research",
  keyword_research: "Keyword Research",
  backlinks_created: "Backlinks Created",
  competitor_analysis: "Competitor Analysis",
  website_content: "Website Content",
  shopify_edit: "Shopify Edit",
  seo_audit: "SEO Audit",
  idea_documentation: "Idea / Strategy Documentation",
  other: "Other",
  // legacy display labels
  backlink_tracking: "Backlink Tracking (legacy)",
  backlink_outreach: "Backlink Outreach (legacy)",
  social_media_tracking: "Social Media Tracking (legacy)",
  traffic_analysis: "Traffic Analysis (legacy)",
  gsc_check: "GSC Check (legacy)",
  image_generation: "Image Generation (legacy)",
};

/** Tools Used field visibility. */
export const SEO_SHOWS_TOOLS: Record<SeoCategory, boolean> = {
  blog_writing: true,
  blog_published: false,
  blog_research: false,
  backlink_research: true,
  keyword_research: true,
  backlinks_created: false,
  competitor_analysis: true,
  website_content: true,
  shopify_edit: false,
  seo_audit: true,
  idea_documentation: false,
  other: false,
  // legacy
  backlink_tracking: false,
  backlink_outreach: false,
  social_media_tracking: false,
  traffic_analysis: false,
  gsc_check: false,
  image_generation: true,
};

/** Status hidden for aggregate / backlinks-created. */
export const SEO_SHOWS_STATUS: Record<SeoCategory, boolean> = {
  blog_writing: true,
  blog_published: true,
  blog_research: false,
  backlink_research: true,
  keyword_research: true,
  backlinks_created: false,
  competitor_analysis: true,
  website_content: true,
  shopify_edit: true,
  seo_audit: true,
  idea_documentation: true,
  other: true,
  // legacy
  backlink_tracking: true,
  backlink_outreach: false,
  social_media_tracking: false,
  traffic_analysis: false,
  gsc_check: false,
  image_generation: false,
};

// SOURCE OF TRUTH: also maintained in lookup_items
// table. When adding new values, add to BOTH here
// and lookup_items, or the DB will accept it but
// the dropdown won't show it.
export const SEO_OTHER_TASK_TYPES = [
  "laptop_issue",
  "network_issue",
  "software_error",
  "tool_subscription_issue",
  "meetings",
  "fun_friday",
  "others",
] as const;
export type SeoOtherTaskType = (typeof SEO_OTHER_TASK_TYPES)[number];
export const SEO_OTHER_TASK_LABEL: Record<SeoOtherTaskType, string> = {
  laptop_issue: "Laptop Issue",
  network_issue: "Network Issue",
  software_error: "Software Error",
  tool_subscription_issue: "Tool Subscription Issue",
  meetings: "Meetings",
  fun_friday: "Fun Friday / Celebration",
  others: "Others",
};

export interface SeoTask {
  id: string;
  user_id: string;
  task_date: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  task_category: AnySeoCategory;
  brand: string | null;
  tools_used: string[];
  status: SeoStatus;
  category_data: Record<string, unknown>;
  notes: string | null;
  correction_status: "pending" | "approved" | "fix";
  correction_note: string | null;
  points: number;
  points_manually_set: boolean;
  created_at: string;
  updated_at: string;
}

export interface SeoOtherTask {
  id: string;
  user_id: string;
  task_date: string;
  start_time: string;
  end_time: string;
  task_type: SeoOtherTaskType;
  notes: string;
  created_at: string;
  updated_at: string;
}

const url = z.string().url("Must be a valid URL");
const optUrl = z.string().url("Must be a valid URL").optional().or(z.literal(""));
const optText = z.string().optional().or(z.literal(""));
const intMin1 = z.coerce.number().int().min(1, "Must be at least 1");
const intMin0 = z.coerce.number().int().min(0);

export const SEO_CATEGORY_SCHEMAS: Record<SeoCategory, z.ZodType<Record<string, unknown>>> = {
  blog_writing: z.object({
    blog_title: z.string().trim().min(1, "Required").max(200),
    word_count: z.coerce.number().int().min(1, "Required"),
    doc_link: url,
    notes: z.string().max(500).optional().or(z.literal("")),
  }),
  blog_published: z.object({
    blog_title: z.string().trim().min(1, "Required").max(200),
    published_url: url,
    notes: optText,
  }),
  backlink_research: z.object({
    research_topic: z.string().trim().min(30, "Please add at least 30 characters"),
    doc_link: url,
    notes: optText,
  }),
  blog_research: z.object({
    research_topic: z.string().trim().min(30, "Please add at least 30 characters"),
    sources: z.array(z.string()).min(1, "Pick at least one source"),
    links: optText,
    doc_link: url,
    notes: optText,
  }),
  keyword_research: z.object({
    keywords_researched: z.coerce
      .number({ invalid_type_error: "Please enter the number of keywords researched" })
      .int("Please enter the number of keywords researched")
      .min(1, "Please enter the number of keywords researched"),
    doc_link: url,
    notes: optText,
  }),
  backlinks_created: z.object({
    num_backlinks_created: intMin1,
    doc_link: url,
    methods: z.array(z.string()).min(1, "Pick at least one method"),
    notes: optText,
  }),
  competitor_analysis: z.object({
    findings: z.string().trim().min(30, "Please add at least 30 characters"),
    doc_link: url,
    notes: optText,
  }),
  website_content: z.object({
    edit_on: z.string().trim().min(1, "Required"),
    what_changed: z.string().trim().min(1, "Required"),
    doc_link: url,
    notes: optText,
  }),
  shopify_edit: z.object({
    store_name: z.string().trim().min(1, "Required"),
    what_was_edited: z.string().trim().min(30, "Please add at least 30 characters"),
    notes: optText,
  }),
  seo_audit: z.object({
    doc_link: optUrl,
    notes: z.string().trim().min(30, "Please add at least 30 characters"),
  }),
  idea_documentation: z.object({
    idea_title: z.string().trim().min(1, "Required"),
    doc_link: url,
    priority: z.enum(["High", "Medium", "Low"]),
    notes: optText,
  }),
  other: z.object({
    description: z.string().trim().min(30, "Please add at least 30 characters"),
    notes: optText,
  }),
  // legacy schemas — accept anything so old rows don't break editing
  backlink_tracking: z.object({}).passthrough(),
  backlink_outreach: z.object({}).passthrough(),
  social_media_tracking: z.object({}).passthrough(),
  traffic_analysis: z.object({}).passthrough(),
  gsc_check: z.object({}).passthrough(),
  image_generation: z.object({}).passthrough(),
};

export const seoTaskSchema = z
  .object({
    start_time: z.string().regex(/^\d{2}:\d{2}$/, "Required"),
    end_time: z.string().regex(/^\d{2}:\d{2}$/, "Required"),
    task_category: z.enum(SEO_CATEGORIES),
    tools_used: z.array(z.string()).default([]),
    status: z.enum(SEO_STATUSES),
    category_data: z.record(z.string(), z.unknown()).default({}),
    notes: optText,
  })
  .refine((d) => d.end_time > d.start_time, {
    message: "End time must be after start time",
    path: ["end_time"],
  })
  .superRefine((val, ctx) => {
    const sch = SEO_CATEGORY_SCHEMAS[val.task_category];
    const result = sch.safeParse(val.category_data);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          ...issue,
          path: ["category_data", ...(issue.path as (string | number)[])],
        });
      }
    }
  });
export type SeoTaskInput = z.infer<typeof seoTaskSchema>;

export const seoOtherTaskSchema = z
  .object({
    start_time: z.string().regex(/^\d{2}:\d{2}$/, "Required"),
    end_time: z.string().regex(/^\d{2}:\d{2}$/, "Required"),
    task_type: z.enum(SEO_OTHER_TASK_TYPES),
    notes: z.string().trim().min(1, "Required").max(1000),
  })
  .refine((d) => d.end_time > d.start_time, {
    message: "End time must be after start time",
    path: ["end_time"],
  });
export type SeoOtherTaskInput = z.infer<typeof seoOtherTaskSchema>;

// ---------- Queries ----------

export async function fetchMySeoTasks(userId: string) {
  const { data, error } = await supabase
    .from("seo_tasks")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .order("start_time", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as SeoTask[];
}

export async function fetchMySeoOtherTasks(userId: string) {
  const { data, error } = await supabase
    .from("seo_other_tasks")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .order("start_time", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as SeoOtherTask[];
}

export async function insertSeoTask(userId: string, v: SeoTaskInput) {
  const { data, error } = await supabase
    .from("seo_tasks")
    .insert({
      user_id: userId,
      start_time: v.start_time,
      end_time: v.end_time,
      task_category: v.task_category,
      brand: null as unknown as string,
      tools_used: v.tools_used,
      status: v.status,
      category_data: v.category_data as never,
      notes: v.notes || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as SeoTask;
}

export async function updateSeoTask(id: string, v: SeoTaskInput) {
  const { data, error } = await supabase
    .from("seo_tasks")
    .update({
      start_time: v.start_time,
      end_time: v.end_time,
      task_category: v.task_category,
      tools_used: v.tools_used,
      status: v.status,
      category_data: v.category_data as never,
      notes: v.notes || null,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as SeoTask;
}

export async function deleteSeoTask(id: string) {
  const { error } = await supabase.from("seo_tasks").delete().eq("id", id);
  if (error) throw error;
}

export async function insertSeoOtherTask(userId: string, v: SeoOtherTaskInput) {
  const { data, error } = await supabase
    .from("seo_other_tasks")
    .insert({
      user_id: userId,
      start_time: v.start_time,
      end_time: v.end_time,
      task_type: v.task_type,
      notes: v.notes,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as SeoOtherTask;
}

export async function updateSeoOtherTask(id: string, v: SeoOtherTaskInput) {
  const { data, error } = await supabase
    .from("seo_other_tasks")
    .update({
      start_time: v.start_time,
      end_time: v.end_time,
      task_type: v.task_type,
      notes: v.notes,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as SeoOtherTask;
}

export async function deleteSeoOtherTask(id: string) {
  const { error } = await supabase.from("seo_other_tasks").delete().eq("id", id);
  if (error) throw error;
}

export function isCurrentMonth(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

export function durationLabel(mins: number): string {
  if (!mins || mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export const SEO_LEGACY_CATEGORY_SET = new Set<string>(SEO_LEGACY_CATEGORIES);

/** A row is legacy if it sits in a deleted category. */
export function isLegacySeoEntry(t: SeoTask): boolean {
  if (SEO_LEGACY_CATEGORY_SET.has(t.task_category)) return true;
  const d = (t.category_data ?? {}) as Record<string, unknown>;
  return d.legacy === true;
}

export function isAggregateSeo(c: SeoCategory): boolean {
  return SEO_AGGREGATE_CATEGORIES.has(c);
}

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);

export function detailsSummary(t: SeoTask): string {
  const d = (t.category_data ?? {}) as Record<string, unknown>;
  const s = (k: string) => (d[k] as string | undefined) ?? "";
  const n = (k: string) => Number((d[k] as number | string | undefined) ?? 0) || 0;
  switch (t.task_category) {
    case "blog_writing":
      return `${s("blog_title")}${d.word_count ? ` (${d.word_count} words)` : ""}`;
    case "blog_published":
      return s("blog_title") || "Blog published";
    case "backlink_research":
      return truncate(s("research_topic") || "Backlink research", 60);
    case "blog_research":
      return truncate(s("research_topic") || `${n("topics_researched")} topics researched`, 60);
    case "keyword_research":
      return s("primary_topic") || `${n("keywords_researched")} keywords researched`;
    case "backlinks_created":
      return `${n("num_backlinks_created")} backlinks created`;
    case "competitor_analysis":
      return truncate(s("findings") || `${n("competitors_analyzed")} competitors analyzed`, 60);
    case "website_content":
      return `${s("edit_on")}: ${truncate(s("what_changed"), 40)}`;
    case "shopify_edit":
      return `${s("store_name")}: ${truncate(s("what_was_edited"), 40)}`;
    case "seo_audit":
      return `Audit · ${(t.tools_used ?? []).join(", ") || "—"}`;
    case "idea_documentation":
      return `${s("idea_title")} (${s("priority")})`;
    case "other":
      return truncate(s("description"), 60);
    // legacy
    case "backlink_tracking":
      return `${n("backlinks_live")} backlinks (legacy)`;
    case "backlink_outreach":
      return `${n("sites_outreached")} sites (legacy)`;
    case "social_media_tracking":
      return `${n("accounts_reviewed")} accounts (legacy)`;
    case "traffic_analysis":
      return `${n("properties_reviewed")} properties (legacy)`;
    case "gsc_check":
      return `${n("properties_checked")} properties (legacy)`;
    case "image_generation":
      return `${n("images_generated")} images (legacy)`;
    default:
      return "";
  }
}

/** Whether the form should show Status select for the given (active) category. */
export function showsStatus(c: SeoCategory): boolean {
  return SEO_SHOWS_STATUS[c];
}
export function showsTools(c: SeoCategory): boolean {
  return SEO_SHOWS_TOOLS[c];
}
